import { type Ref } from "vue"
import { BaseModel } from "@/models/base.model"
import ModelManager from "@/services/model-manager"
import type { BaseRepository } from "@/repositories/base-repository"
import { type IBase } from "@/interfaces/api/base.interface"
import { cloneDeep } from "lodash-es"
import { type IRepositoryRequestConfig } from "@/interfaces/repository/repository-request-config.interface"
import { type ModelConstructorSignature } from "@/models/types/model-constructor-signature"
import { ModelFactory } from "@/factories/model-factory"

export function useStoreBaseFunctions<
  T extends IBase,
  U extends BaseModel,
  V extends BaseRepository<T>
>(
  modelType: ModelConstructorSignature<T, U>,
  repositoryType: new () => V,
  entireCollection?: Ref<BaseModel[]>,
  collectionFinder?: (model: U) => U[] | null
) {
  async function fetchModels(config: IRepositoryRequestConfig = {}) {
    if (!entireCollection?.value) {
      throw Error("entireCollection parameter is not defined.")
    }

    const result = await new repositoryType().getList(config)
    const data = result.data as T[]
    const models = ModelFactory.createModels(modelType, data)
    // Await any pending managed field resolutions before proceeding.
    await BaseModel.whenFullyResolved(models)

    entireCollection.value.push(...models)
  }

  async function saveModel(data: T, config: IRepositoryRequestConfig = {}) {
    const result = await new repositoryType().create(data, config)
    const instance = ModelFactory.createModel(modelType, result.data)

    if (entireCollection?.value) {
      entireCollection.value.push(instance)
    }
    return instance
  }

  async function updateModel(instance: T, config: IRepositoryRequestConfig = {}) {
    const modelCopy = cloneDeep(instance)
    if (!modelCopy.id) {
      throw new Error("Cannot update model without id.")
    }

    const result = await new repositoryType().update(modelCopy.id, modelCopy, config)
    return ModelFactory.createModel(modelType, result.data)
  }

  async function deleteModel(instance: U, config: IRepositoryRequestConfig = {}) {
    const modelCopy = { ...instance }

    /* Delete remotely */
    if (modelCopy.id) {
      const result = await new repositoryType().delete(modelCopy.id, config)
      if (result.status !== 204) {
        return // Deletion was not successful, and error was handled. So do not proceed with local deletion.
      }
    }

    ModelManager.getInstance().delete(modelCopy.id!)

    /* Delete locally */
    if (collectionFinder) {
      /* If we are dealing with a nested helper, like instructions or sales */
      const collection = collectionFinder(instance)
      if (collection) {
        const index = collection.findIndex((item) => item.id === instance.id)
        if (index !== -1) {
          collection.splice(index, 1)
        }
      }
    } else if (entireCollection?.value) {
      /* Or we are dealing with a simple standalone helper, like users or machines */
      const index = entireCollection.value.findIndex((item) => item.id === instance.id)
      if (index !== -1) {
        entireCollection.value.splice(index, 1)
      }
    }

    return true
  }

  return {
    fetchModels,
    saveModel,
    updateModel,
    deleteModel
  }
}
